import {useState, useReducer, useEffect} from 'react'
import {gql, useQuery, useMutation} from '@apollo/client'
import {Elements} from '@stripe/react-stripe-js'
import {loadStripe} from '@stripe/stripe-js'

import LoadingSpinner from '../LoadingSpinner'
import ErrorText from '../ErrorText'
import AddPaymentMethod from './AddPaymentMethod'
import CreditCard from '../CreditCard'
import TextInput from '../inputs/TextInput'
import Button from '../inputs/Button'
import CheckPaymentMethodStatus from './CheckPaymentMethodStatus'
import {
  reducer,
  initialState,
  CLEAR_SUCCESS,
  START_ACTION,
} from '../../reducers/actionReducer'
import ActionModal from '../ActionModal'
import ToastNotification from '../ToastNotification'
import {CUSTOMER_QUERY} from '../../queries'
import DeletePaymentMethod from '../actions/DeletePaymentMethod'
import FormItem from '../form/FormItem'

const SET_DEFAULT_PAYMENT_METHOD_MUTATION = gql`
  mutation SetDefaultPaymentMethod($pmId: String!) {
    setDefaultPaymentMethod(pmId: $pmId) {
      message
    }
  }
`

const STRIPE_PK = import.meta.env.VITE_STRIPE_PK
const stripePromise = loadStripe(STRIPE_PK)

function BillingDetails({billing_details}) {
  const {address} = billing_details
  const {country, postal_code} = address

  return (
    <div className='flex flex-col pt-6'>
      <form className='space-y-3'>
        <p className='font-semibold text-lg'>Billing Information</p>
        <FormItem label='Country'>
          <TextInput
            type='text'
            placeholder='Country'
            value={country}
            disabled
          />
        </FormItem>
        <FormItem label='Zip Code'>
          <TextInput
            type='text'
            placeholder='Zip Code'
            value={postal_code}
            disabled
          />
        </FormItem>
      </form>
    </div>
  )
}

export default function UserProfilePaymentMethods() {
  const [selectedCard, setSelectedCard] = useState(0)
  const {loading, error, data} = useQuery(CUSTOMER_QUERY)
  const customer = data?.customer

  const [
    setDefaultPaymentMethod,
    {
      loading: loadingSetDefaultPaymentMethod,
      error: errorSetDefaultPaymentMethod,
    },
  ] = useMutation(SET_DEFAULT_PAYMENT_METHOD_MUTATION, {
    refetchQueries: [CUSTOMER_QUERY],
  })

  const [actionState, dispatchAction] = useReducer(reducer, initialState)
  const {success} = actionState

  useEffect(() => {
    if (success) {
      setTimeout(
        () =>
          dispatchAction({
            type: CLEAR_SUCCESS,
          }),
        5000
      )
    }
  }, [success])

  function handleCreditCardClick(idx) {
    setSelectedCard(idx)
  }

  function handleClickDelete() {
    const pmId = customer.paymentMethods[selectedCard]?.id
    const last4 = customer.paymentMethods[selectedCard]?.card?.last4
    dispatchAction({
      type: START_ACTION,
      payload: {
        name: 'Delete Card',
        component: (
          <DeletePaymentMethod
            dispatchAction={dispatchAction}
            pmId={pmId}
            last4={last4}
          />
        ),
      },
    })
  }

  function handleClickMakeDefault() {
    const pmId = customer.paymentMethods[selectedCard]?.id
    setDefaultPaymentMethod({variables: {pmId}})
  }

  return (
    <>
      <div className='flex flex-col pt-4 w-full overflow-auto'>
        {loading && <LoadingSpinner message='Getting payment details...' />}
        {error &&
          (error.message === 'stripe-customer-does-not-exist' ? (
            <div className='flex flex-col'>
              <p>
                You haven't set up payments yet. Please add a payment method
                below.
              </p>
              <AddPaymentMethod className='pt-4' />
            </div>
          ) : (
            <ErrorText message={error.message} />
          ))}
        {!loading && !error && customer && (
          <>
            <div className='flex flex-col w-full'>
              {customer.paymentMethods?.length === 0 && (
                <p>No saved payment methods.</p>
              )}
              {customer.paymentMethods?.length > 0 && (
                <>
                  <div className='flex flex-row w-full overflow-auto'>
                    {customer.paymentMethods.map(({id, card}, idx) => (
                      <CreditCard
                        className='mr-6'
                        last4={card.last4}
                        exp={`${card.exp_month} / ${card.exp_year}`}
                        brand={card.brand}
                        key={id}
                        onClick={() => handleCreditCardClick(idx)}
                        active={idx === selectedCard}
                        isDefault={card.isDefault}
                      />
                    ))}
                  </div>
                  <BillingDetails
                    billing_details={
                      customer.paymentMethods[selectedCard].billing_details
                    }
                  />
                  <div className='flex mt-6 mb-10 space-x-2'>
                    <Button type='button' secondary onClick={handleClickDelete}>
                      Delete
                    </Button>
                    {!customer.paymentMethods[selectedCard]?.card
                      ?.isDefault && (
                      <Button
                        type='button'
                        secondary
                        onClick={handleClickMakeDefault}
                        disabled={loadingSetDefaultPaymentMethod}
                        className={
                          loadingSetDefaultPaymentMethod ? 'animate-pulse' : ''
                        }
                      >
                        Make Default
                      </Button>
                    )}
                  </div>
                  {errorSetDefaultPaymentMethod && (
                    <ErrorText
                      message={errorSetDefaultPaymentMethod.message}
                      className='ml-0'
                    />
                  )}
                </>
              )}
            </div>
            <AddPaymentMethod className='pt-4' />
          </>
        )}
        <Elements stripe={stripePromise}>
          <CheckPaymentMethodStatus />
        </Elements>
      </div>
      {actionState.inProgress && (
        <ActionModal
          actionState={actionState}
          dispatchAction={dispatchAction}
          show={actionState.inProgress}
        />
      )}
      <ToastNotification
        message={actionState.success}
        success={true}
        show={actionState.success}
      />
    </>
  )
}
